import { inject, Injectable } from '@angular/core';
import { ApiServiceBase, RequestMethod } from '@api/abstracts';
import { CacheService } from '@services/cache';
import { ContextService } from '@services/context';
import { iconList } from '@services/icons/config';
import { LogsService } from '@services/logs';
import { AvailableIcons } from '@shared/enums';
import { catchError, from, mergeMap, Observable, of, take } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class IconsService extends ApiServiceBase<string> {
  readonly #cacheService = inject(CacheService);
  readonly #logsService = inject(LogsService);

  constructor() {
    super('');
  }

  loadOnCache(): void {
    if (inject(ContextService).isServer()) return;
    iconList.map((icon) => {
      this.getIcons(icon).pipe(take(1)).subscribe();
    });
  }

  getIcons(iconName: keyof typeof AvailableIcons): Observable<string> {
    const name: string = AvailableIcons[iconName] ?? undefined;
    if (!name) return of('');
    return from(this.#cacheService.get(`ICON_${iconName}`) as Promise<string>).pipe(
      mergeMap((icon) => {
        if (icon) return of(icon);
        return this.customRequest<string>(
          RequestMethod.GET,
          `/assets/icons/${name}.svg`,
          {
            responseType: 'text',
          },
          { cacheExpirationTime: 3600 },
          { external: true }
        ).pipe(
          tap((icon) => {
            this.#cacheService
              .set(`ICON_${iconName}`, icon)
              .catch((error: unknown) =>
                this.#logsService.warn('IconsService', 'Failed to cache icon', iconName, error)
              );
          })
        );
      }),
      catchError((err: unknown) => {
        this.#logsService.error('IconsService', 'Icon fetch failed', iconName, err);
        return of('');
      })
    );
  }
}
